"use client";

import React, { memo, useCallback, useMemo } from "react";
import type { ReactNode } from "react";
import { Box, SegmentGroup } from "@chakra-ui/react";

interface GenericSegmentProps<T extends string = string> {
  activeState: T;
  setActiveState: (value: T) => void;
  options: { value: T; label: ReactNode }[];
  size?: "xs" | "sm" | "md";
  disabled?: boolean;
  orientation?: "horizontal" | "vertical";
}

function GenericSegmentInner<T extends string = string>({
  activeState,
  setActiveState,
  options,
  size = "md",
  disabled = false,
  orientation = "horizontal",
}: GenericSegmentProps<T>) {
  // ✅ Memoized handler to prevent re-creation on each render
  const handleValueChange = useCallback(
    (e: { value: string | null }) => {
      if (e.value !== null) {
        setActiveState(e.value as T);
      }
    },
    [setActiveState],
  );

  // ✅ Memoized options (only rebuild when options change)
  const memoizedOptions = useMemo(() => options, [options]);

  return (
    <Box>
      <SegmentGroup.Root
        size={size}
        value={activeState}
        onValueChange={handleValueChange}
        disabled={disabled}
        orientation={orientation}
      >
        <SegmentGroup.Indicator />
        <SegmentGroup.Items items={memoizedOptions} />
      </SegmentGroup.Root>
    </Box>
  );
}

// ✅ Prevent re-renders when props are identical
const GenericSegment = memo(GenericSegmentInner) as <T extends string = string>(
  props: GenericSegmentProps<T>,
) => React.ReactElement;

export default GenericSegment;
